import { Edit01Icon, Trash01Icon } from '@langchain/untitled-ui-icons';

import { useState } from 'react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/Select/Select';
import { TextOverflowTooltip } from '@/components/TextOverflowTooltip';
import { Button, IconButton, Input, Text } from '@/design-system';
import { DatasetDataType, ExampleSchema } from '@/types/schema';

type MetadataValueType = 'string' | 'number' | 'boolean';

function inferMetadataValueType(value: unknown): MetadataValueType {
  if (typeof value === 'number') return 'number';
  if (typeof value === 'boolean') return 'boolean';
  return 'string';
}

export function ExampleMetadataDisplay(props: {
  metadata: ExampleSchema['metadata'];
  dataType: DatasetDataType;
  readOnly: boolean;
  onMutate?: (newMetadata: ExampleSchema['metadata']) => void;
}) {
  const metadataEntries = Object.entries(props.metadata ?? {}).filter(
    ([key]) => key !== 'dataset_split'
  );
  const [isAddingMetadata, setIsAddingMetadata] = useState(false);

  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const [valueType, setValueType] = useState<MetadataValueType>('string');

  const handleAddMetadataButtonClick = () =>
    setIsAddingMetadata((prev) => !prev);

  return (
    <div className="flex w-full flex-col gap-2 rounded-xl border border-secondary p-4">
      <div className="flex justify-between">
        <Text variant="h2">Metadata</Text>
        {!props.readOnly && (
          <Button
            size="md"
            color="primary"
            variant="outlined"
            onClick={handleAddMetadataButtonClick}
            disabled={props.readOnly || isAddingMetadata}
          >
            Add Metadata
          </Button>
        )}
      </div>
      <div className="flex flex-col divide-y divide-secondary">
        {isAddingMetadata && (
          <form
            className="flex gap-4 p-4"
            onSubmit={(e) => {
              e.preventDefault();
              let parsedValue: string | number | boolean = value;
              if (valueType === 'number') {
                parsedValue = Number(value);
              } else if (valueType === 'boolean') {
                parsedValue = value.toLowerCase() === 'true';
              }
              setKey('');
              setValue('');
              setValueType('string');
              setIsAddingMetadata(false);
              props.onMutate?.({
                ...props.metadata,
                [key]: parsedValue,
              });
            }}
          >
            <div className="flex w-[20%] overflow-hidden text-ellipsis">
              <Input
                type="text"
                placeholder="Key"
                onChange={(e) => setKey(e)}
                value={key}
              />
            </div>
            {valueType === 'boolean' ? (
              <Select
                value={value}
                onValueChange={(newValue) => setValue(newValue)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="true/false" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">true</SelectItem>
                  <SelectItem value="false">false</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <Input
                type="text"
                placeholder="Value"
                onChange={(e) => setValue(e)}
                value={value}
                className="w-[360px]"
              />
            )}
            <Select
              value={valueType}
              onValueChange={(newValue) => {
                setValueType(newValue as MetadataValueType);
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="string">String</SelectItem>
                <SelectItem value="number">Number</SelectItem>
                <SelectItem value="boolean">Boolean</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <Button
                size="md"
                color="primary"
                disabled={
                  key === '' ||
                  (valueType === 'boolean' &&
                    value !== 'true' &&
                    value !== 'false') ||
                  (valueType === 'number' &&
                    (isNaN(Number(value)) || value === ''))
                }
                type="submit"
              >
                Add
              </Button>
              <Button
                size="md"
                color="secondary"
                variant="outlined"
                type="button"
                onClick={() => setIsAddingMetadata(false)}
              >
                Cancel
              </Button>
            </div>
          </form>
        )}
        {metadataEntries.length === 0 && !isAddingMetadata ? (
          <Text variant="body" color="tertiary">
            No metadata associated with this example
          </Text>
        ) : (
          metadataEntries.map(([key, value]) => (
            <MetadataEntry
              key={key}
              readOnly={props.readOnly}
              metadataKey={key}
              value={value}
              onEditSubmit={(newKey, newValue) => {
                const newMetadata = { ...props.metadata };
                if (newValue != null) {
                  if (newKey !== key) {
                    delete newMetadata[key];
                  }
                  newMetadata[newKey] = newValue;
                } else {
                  delete newMetadata[newKey];
                }
                props.onMutate?.(newMetadata);
              }}
            />
          ))
        )}
      </div>
    </div>
  );
}

function MetadataEntry(props: {
  metadataKey: string;
  value: unknown;
  loading?: boolean;
  onEditSubmit: (key: string, value: string | number | boolean | null) => void;
  readOnly: boolean;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editedKey, setEditedKey] = useState(props.metadataKey);
  const [editedValue, setEditedValue] = useState(
    typeof props.value === 'string' ||
      typeof props.value === 'number' ||
      typeof props.value === 'boolean'
      ? `${props.value}`
      : ''
  );
  const [editedValueType, setEditedValueType] = useState<MetadataValueType>(
    inferMetadataValueType(props.value)
  );

  const canEdit =
    typeof props.value === 'string' ||
    typeof props.value === 'number' ||
    typeof props.value === 'boolean';
  const valueToDisplay = (
    canEdit
      ? props.value
      : props.value == null
      ? ''
      : JSON.stringify(props.value)
  ) as string | number | boolean;

  return (
    <form
      className="group flex w-full items-center gap-6 p-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (canEdit) {
          let parsedValue: string | number | boolean = editedValue;
          if (editedValueType === 'number') {
            parsedValue = Number(editedValue);
          } else if (editedValueType === 'boolean') {
            parsedValue = editedValue.toLowerCase() === 'true';
          }
          props.onEditSubmit(editedKey, parsedValue);
          setIsEditing(false);
        }
      }}
    >
      {isEditing ? (
        <div className="flex w-full items-center gap-2">
          <div className="flex w-[20%] overflow-hidden text-ellipsis">
            <Input value={editedKey} onChange={(e) => setEditedKey(e)} />
          </div>
          {editedValueType === 'boolean' ? (
            <Select
              value={editedValue}
              onValueChange={(newValue) => setEditedValue(newValue)}
            >
              <SelectTrigger>
                <SelectValue placeholder="true/false" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="true">true</SelectItem>
                <SelectItem value="false">false</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <Input
              value={editedValue}
              onChange={(e) => setEditedValue(e)}
              width="360px"
            />
          )}
          <Select
            value={editedValueType}
            onValueChange={(newValue) => {
              setEditedValueType(newValue as MetadataValueType);
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="string">String</SelectItem>
              <SelectItem value="number">Number</SelectItem>
              <SelectItem value="boolean">Boolean</SelectItem>
            </SelectContent>
          </Select>
          <Button
            size="md"
            color="primary"
            type="submit"
            loading={props.loading}
            disabled={
              (editedValueType === 'boolean' &&
                editedValue !== 'true' &&
                editedValue !== 'false') ||
              (editedValueType === 'number' &&
                (isNaN(Number(editedValue)) || editedValue === ''))
            }
          >
            Save
          </Button>
          <Button
            size="md"
            color="secondary"
            onClick={() => setIsEditing(false)}
            variant="outlined"
          >
            Cancel
          </Button>
        </div>
      ) : (
        <>
          <TextOverflowTooltip className="w-[20%]" textVariant="h3">
            {props.metadataKey}
          </TextOverflowTooltip>
          <TextOverflowTooltip className="w-[75%] font-normal" textVariant="h3">
            {valueToDisplay.toString()}
          </TextOverflowTooltip>
          {!props.readOnly && (
            <div className="pointer-events-none flex gap-3 opacity-0 group-hover:pointer-events-auto group-hover:opacity-100">
              {canEdit && (
                <IconButton
                  icon={Edit01Icon}
                  size="sm"
                  color="secondary"
                  variant="outlined"
                  onClick={() => {
                    setIsEditing(true);
                    setIsDeleting(false);
                  }}
                  label="Edit"
                />
              )}
              {isDeleting ? (
                <Button
                  size="sm"
                  color="error"
                  variant="outlined"
                  loading={props.loading}
                  onClick={() => props.onEditSubmit(props.metadataKey, null)}
                >
                  Delete
                </Button>
              ) : (
                <IconButton
                  icon={Trash01Icon}
                  size="sm"
                  color="secondary"
                  variant="outlined"
                  onClick={() => setIsDeleting(true)}
                  label="Delete"
                />
              )}
            </div>
          )}
        </>
      )}
    </form>
  );
}
